import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router";
import { ProtectedRoute } from "@/lib/protected-route";
import { AppLayout } from "@/app/layout";
import { AppLoader } from "@/components/app-loader";

// Public pages
const LoginPage = lazy(() => import("@/app/pages/login").then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import("@/app/pages/register").then((m) => ({ default: m.RegisterPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/app/pages/reset-password").then((m) => ({ default: m.ResetPasswordPage })),
);
const PublicProfilePage = lazy(() =>
  import("@/app/pages/public-profile").then((m) => ({ default: m.PublicProfilePage })),
);
const LandingPage = lazy(() =>
  import("@/app/pages/landing").then((m) => ({ default: m.LandingPage })),
);
const VerifyEmailPage = lazy(() =>
  import("@/app/pages/verify-email").then((m) => ({ default: m.VerifyEmailPage })),
);

// Authenticated pages
const CatalogPage = lazy(() =>
  import("@/app/pages/catalog").then((m) => ({ default: m.CatalogPage })),
);
const CatalogNewPage = lazy(() =>
  import("@/app/pages/catalog-new").then((m) => ({ default: m.CatalogNewPage })),
);
const CatalogDetailPage = lazy(() =>
  import("@/app/pages/catalog-detail").then((m) => ({ default: m.CatalogDetailPage })),
);
const CatalogEditPage = lazy(() =>
  import("@/app/pages/catalog-edit").then((m) => ({ default: m.CatalogEditPage })),
);
const AssistantPage = lazy(() =>
  import("@/app/pages/assistant").then((m) => ({ default: m.AssistantPage })),
);
const IngestPage = lazy(() =>
  import("@/app/pages/ingest").then((m) => ({ default: m.IngestPage })),
);
const IngestNewPage = lazy(() =>
  import("@/app/pages/ingest-new").then((m) => ({ default: m.IngestNewPage })),
);
const IngestScanPage = lazy(() =>
  import("@/app/pages/ingest-scan").then((m) => ({ default: m.IngestScanPage })),
);
const NeighborsPage = lazy(() =>
  import("@/app/pages/neighbors").then((m) => ({ default: m.NeighborsPage })),
);
const YouPage = lazy(() => import("@/app/pages/you").then((m) => ({ default: m.YouPage })));
const NeighborProfilePage = lazy(() =>
  import("@/app/pages/neighbor-profile").then((m) => ({ default: m.NeighborProfilePage })),
);
const SettingsPage = lazy(() =>
  import("@/app/pages/settings").then((m) => ({ default: m.SettingsPage })),
);
const AdminPage = lazy(() => import("@/app/pages/admin").then((m) => ({ default: m.AdminPage })));

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/n/:slug" element={<PublicProfilePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Landing — redirects authenticated users to /assistant */}
        <Route path="/" element={<LandingPage />} />

        {/* Authenticated routes */}
        <Route
          path="/catalog"
          element={
            <AuthenticatedLayout>
              <CatalogPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/catalog/new"
          element={
            <AuthenticatedLayout>
              <CatalogNewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/catalog/:id"
          element={
            <AuthenticatedLayout>
              <CatalogDetailPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/catalog/:id/edit"
          element={
            <AuthenticatedLayout>
              <CatalogEditPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/ingest"
          element={
            <AuthenticatedLayout>
              <IngestPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/ingest/new"
          element={
            <AuthenticatedLayout>
              <IngestNewPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/ingest/:scanId"
          element={
            <AuthenticatedLayout>
              <IngestScanPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/assistant"
          element={
            <AuthenticatedLayout>
              <AssistantPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/you"
          element={
            <AuthenticatedLayout>
              <YouPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/neighbors"
          element={
            <AuthenticatedLayout>
              <NeighborsPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/neighbors/:slug"
          element={
            <AuthenticatedLayout>
              <NeighborProfilePage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthenticatedLayout>
              <SettingsPage />
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/admin"
          element={
            <AuthenticatedLayout>
              <AdminPage />
            </AuthenticatedLayout>
          }
        />
      </Routes>
    </Suspense>
  );
}
